import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtNum, formatErr } from "@/lib/api";
import { mergeMetrics } from "@/lib/liveMetrics";
import { useWS } from "@/contexts/WebSocketContext";
import { useAuth } from "@/contexts/AuthContext";
import { MagnifyingGlass, CaretUpDown, Circle, Lightning, Pause, Play, PlugsConnected, X, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import RefreshButton from "@/components/layout/RefreshButton";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";
import AccountIdentity from "@/components/AccountIdentity";
import GroupSelect from "@/components/GroupSelect";
import { SessionMetricCells, SessionMetricGrid } from "@/components/SessionMetrics";
import { downloadReport } from "@/lib/accountGroups";

function AccountStatCard({ label, value, sub, accent = "white", active, onClick, testid }) {
  const accentClass =
    accent === "green" ? "text-[#10B981]" :
    accent === "muted" ? "text-app-muted" :
    accent === "orange" ? "text-[#FF9500]" : "text-app-text";

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left border bg-app-surface p-4 sm:p-5 min-w-0 transition-colors touch-target ${
        active ? "border-[#007AFF]/50 bg-[#007AFF]/5" : "border-app-border hover:bg-app-hover hover:border-app-border-strong"
      }`}
    >
      <div className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-app-subtle mb-2">{label}</div>
      <div className={`font-mono-d text-[clamp(1.5rem,5vw,2rem)] font-semibold tabular-nums ${accentClass}`}>
        {value}
      </div>
      {sub && <div className="font-mono-d text-[10px] text-app-muted mt-2 uppercase tracking-wider">{sub}</div>}
    </button>
  );
}

function AccountCard({ account: a, canControl, canRemove, selected, onToggle, onOpen, onRemove, groups, onGroupChange }) {
  const m = a.metrics || {};

  return (
    <div
      className="border border-app-border bg-app-surface p-4 cursor-pointer hover:bg-app-hover transition-colors"
      data-testid={`account-card-${a.account_number}`}
      onClick={onOpen}
    >
      <div className="flex items-start gap-3 mb-3">
        {canControl && (
          <input
            type="checkbox"
            data-testid={`row-check-${a.account_number}`}
            checked={selected}
            onChange={(e) => { e.stopPropagation(); onToggle(); }}
            onClick={(e) => e.stopPropagation()}
            className="accent-[#007AFF] mt-1 touch-target shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <AccountIdentity
            group={a.group}
            groupColor={a.group_color}
            accountNumber={a.account_number}
            accountName={a.account_name}
            broker={a.broker}
            showBroker
            bold
          />
        </div>
        <span className={`shrink-0 px-2 py-0.5 font-mono-d text-[10px] uppercase tracking-wider border ${
          !m.ea_connected ? "border-[#666]/40 text-app-muted" :
          m.ea_running ? "border-[#10B981]/40 text-[#10B981]" : "border-[#FF9500]/40 text-[#FF9500]"
        }`}>
          {!m.ea_connected ? "Offline" : m.ea_running ? "Running" : "Paused"}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onRemove(a.id, a.account_number); }}
            data-testid={`del-card-${a.account_number}`}
            className="btn-touch shrink-0 p-2 border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10"
            title="Remove account"
          >
            <Trash size={14} />
          </button>
        )}
      </div>
      <SessionMetricGrid m={m} expanded />
      {canControl && (
        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
          <GroupSelect
            compact
            value={a.group}
            groups={groups}
            onChange={(g) => onGroupChange(a.id, g)}
          />
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-app-border/70 flex items-center gap-1.5 font-mono-d text-[10px] uppercase">
        <Circle size={8} weight="fill" className={m.vps_online ? "text-[#10B981]" : "text-[#FF3B30]"} />
        VPS {m.vps_online ? `${m.ping ?? 0}ms` : "Offline"}
      </div>
    </div>
  );
}

export default function Accounts() {
  const { registrationTick, status: wsStatus, liveMetrics, liveVersion } = useWS();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [sortBy, setSortBy] = useState("equity");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const [error, setError] = useState(null);
  const [checkingConnections, setCheckingConnections] = useState(false);
  const perPage = 10;

  const load = async () => {
    try {
      const [{ data }, { data: grp }] = await Promise.all([
        api.get("/accounts"),
        api.get("/groups"),
      ]);
      setAccounts(data);
      setGroups(grp);
      setError(null);
    } catch (e) {
      setError("Failed to load accounts");
      toast.error("Failed to load accounts");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, [registrationTick]);
  useEffect(() => {
    const interval = wsStatus === "connected" ? 60000 : 10000;
    const t = setInterval(load, interval);
    return () => clearInterval(t);
  }, [wsStatus]);

  const displayAccounts = useMemo(
    () => accounts.map((a) => ({
      ...a,
      metrics: mergeMetrics(a.metrics, liveMetrics[a.id]),
    })),
    [accounts, liveMetrics, liveVersion],
  );

  const isEaRunning = (a) => Boolean(a.metrics?.ea_connected && a.metrics?.ea_running);

  const accountStats = useMemo(() => {
    const total = displayAccounts.length;
    const running = displayAccounts.filter(isEaRunning).length;
    return {
      total,
      running,
      notRunning: total - running,
    };
  }, [displayAccounts]);

  const filtered = useMemo(() => {
    let list = displayAccounts;
    if (search) list = list.filter(a => `${a.account_number} ${a.broker} ${a.account_name} ${a.group || ""}`.toLowerCase().includes(search.toLowerCase()));
    if (filter === "active") list = list.filter(a => a.enabled);
    if (filter === "disabled") list = list.filter(a => !a.enabled);
    if (filter === "ea_running") list = list.filter(isEaRunning);
    if (filter === "ea_not_running") list = list.filter((a) => !isEaRunning(a));
    if (filter === "ea_paused") list = list.filter(a => a.metrics?.ea_connected && !a.metrics?.ea_running);
    if (filter === "ea_offline") list = list.filter(a => !a.metrics?.ea_connected);
    if (groupFilter !== "all") list = list.filter(a => (a.group || "") === groupFilter);
    list = [...list].sort((a, b) => {
      const av = a.metrics?.[sortBy] ?? 0; const bv = b.metrics?.[sortBy] ?? 0;
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return list;
  }, [displayAccounts, search, filter, groupFilter, sortBy, sortDir]);
  useEffect(() => { setPage(1); }, [search, filter, groupFilter]);

  const pageList = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  const toggle = (id) => {
    const ns = new Set(selected);
    ns.has(id) ? ns.delete(id) : ns.add(id);
    setSelected(ns);
  };

  const runBulk = async (cmd) => {
    if (selected.size === 0) return toast.error("Select accounts first");
    if (!window.confirm(`Apply "${cmd}" to ${selected.size} accounts?`)) return;
    try {
      await api.post("/accounts/bulk/command", { account_ids: [...selected], command: cmd, params: {} });
      toast.success(`Bulk command ${cmd} applied to ${selected.size} accounts`);
      setSelected(new Set());
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const manualRefresh = async () => {
    await load();
    toast.success("Accounts refreshed");
  };

  const checkConnections = async () => {
    if (checkingConnections) return;
    setCheckingConnections(true);
    try {
      const { data } = await api.get("/accounts/connections");
      await load();
      const labels = (data.connected || []).map((a) => `#${a.account_number}`).join(", ");
      if (data.any_connected) {
        toast.success(
          `${data.connected_count} EA${data.connected_count === 1 ? "" : "s"} connected${labels ? `: ${labels}` : ""}`,
          { duration: 6000 },
        );
      } else {
        toast.warning(
          data.total_accounts
            ? `No EAs connected — ${data.total_accounts} account${data.total_accounts === 1 ? "" : "s"} registered`
            : "No accounts registered yet",
          { duration: 6000 },
        );
      }
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Connection check failed");
    } finally {
      setCheckingConnections(false);
    }
  };

  const canControl = user?.role !== "viewer";
  const isAdmin = user?.role === "admin";
  const sortHeaders = {
    "Start Bal": "starting_balance",
    "Close Bal": "closing_balance",
    "Max DD": "max_drawdown_today",
    "Profit (Today)": "profit_today",
  };

  const assignGroup = async (accountId, group) => {
    try {
      await api.patch(`/accounts/${accountId}`, { group: group || null });
      await load();
      toast.success(group ? `Assigned to ${group}` : "Group cleared");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to update group");
    }
  };

  const handleExport = async (scope) => {
    try {
      await downloadReport(scope);
      toast.success(scope === "today" ? "Today's report downloaded" : "All-time report downloaded");
    } catch {
      toast.error("Failed to download report");
    }
  };

  const removeAccount = async (id, accountNumber) => {
    if (!window.confirm(`Permanently remove account #${accountNumber}? This deletes all metrics, trades, and history.`)) return;
    try {
      await api.delete(`/accounts/${id}`);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await load();
      toast.success(`Account #${accountNumber} removed`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to remove account");
    }
  };

  const bulkRemove = async () => {
    if (selected.size === 0) return toast.error("Select accounts first");
    if (!window.confirm(`Permanently remove ${selected.size} account(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/accounts/bulk/delete", { account_ids: [...selected] });
      toast.success(`Removed ${data.deleted} account(s)`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to remove accounts");
    }
  };

  return (
    <PageShell>
      <PageHeader eyebrow="Account Grid" title="ACCOUNTS">
        <div className="flex flex-col items-stretch sm:items-end gap-2.5 w-full lg:w-auto">
          <span
            className={`inline-flex self-end items-center gap-1.5 px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border ${
              wsStatus === "connected"
                ? "border-[#10B981]/35 text-[#10B981] bg-[#10B981]/8"
                : "border-[#FF3B30]/35 text-[#FF3B30] bg-[#FF3B30]/8"
            }`}
          >
            <Circle size={6} weight="fill" />
            Floating P/L · {wsStatus === "connected" ? "Live ~1s" : "Poll 10s"}
          </span>
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <button
              type="button"
              data-testid="accounts-check-connections-btn"
              onClick={checkConnections}
              disabled={checkingConnections}
              title="Check EA connections"
              className="btn-touch inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-mono-d uppercase tracking-widest border border-app-border bg-app-surface hover:bg-app-hover hover:border-app-border-strong disabled:opacity-50 transition-colors"
            >
              <PlugsConnected size={14} className={checkingConnections ? "animate-pulse" : ""} />
              {checkingConnections ? "Checking…" : "Check EA"}
            </button>
            <RefreshButton onRefresh={manualRefresh} testId="accounts-refresh-btn" />
            <button
              type="button"
              onClick={() => handleExport("today")}
              className="btn-touch text-xs px-3 py-2 border border-app-border bg-app-surface hover:bg-app-hover font-mono-d uppercase tracking-wider"
            >
              Today XLS
            </button>
            <button
              type="button"
              onClick={() => handleExport("all_time")}
              className="btn-touch text-xs px-3 py-2 border border-app-border bg-app-surface hover:bg-app-hover font-mono-d uppercase tracking-wider"
            >
              All-Time XLS
            </button>
          </div>
        </div>
      </PageHeader>

      <div className="page-filter-bar mb-6">
        <div className="page-filter-field flex-1 min-w-0">
          <label htmlFor="accounts-search" className="page-filter-label">Search</label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-app-muted pointer-events-none" />
            <input
              id="accounts-search"
              data-testid="search-input"
              placeholder="Account, broker, name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-app-bg border border-app-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-app-accent min-h-[40px]"
            />
          </div>
        </div>
        <div className="page-filter-field w-full sm:w-40 lg:w-44">
          <label htmlFor="accounts-group-filter" className="page-filter-label">Group</label>
          <select
            id="accounts-group-filter"
            data-testid="group-filter"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm min-h-[40px] focus:outline-none focus:border-app-accent"
          >
            <option value="all">All groups</option>
            {groups.map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
          </select>
        </div>
        <div className="page-filter-field w-full sm:w-44 lg:w-48">
          <label htmlFor="accounts-status-filter" className="page-filter-label">Status</label>
          <select
            id="accounts-status-filter"
            data-testid="filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm min-h-[40px] focus:outline-none focus:border-app-accent"
          >
            <option value="all">All accounts</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="ea_running">EA running</option>
            <option value="ea_not_running">EA not running</option>
            <option value="ea_paused">EA paused</option>
            <option value="ea_offline">EA offline</option>
          </select>
        </div>
      </div>

      <div className="responsive-grid mb-4 sm:mb-6" data-testid="accounts-status-summary">
        <AccountStatCard
          label="Total Accounts"
          value={accountStats.total}
          sub="All registered"
          active={filter === "all" && groupFilter === "all" && !search}
          onClick={() => { setFilter("all"); setGroupFilter("all"); setSearch(""); }}
          testid="stat-total-accounts"
        />
        <AccountStatCard
          label="Running Accounts"
          value={accountStats.running}
          sub="EA connected & running"
          accent="green"
          active={filter === "ea_running"}
          onClick={() => setFilter("ea_running")}
          testid="stat-running-accounts"
        />
        <AccountStatCard
          label="Not Running Accounts"
          value={accountStats.notRunning}
          sub="Offline or paused"
          accent="orange"
          active={filter === "ea_not_running"}
          onClick={() => setFilter("ea_not_running")}
          testid="stat-not-running-accounts"
        />
      </div>

      {canControl && selected.size > 0 && (
        <div className="mb-4 border border-[#007AFF]/40 bg-[#007AFF]/5 p-3 flex flex-wrap items-center gap-2 sm:gap-3" data-testid="bulk-bar">
          <Lightning size={14} className="text-[#007AFF] shrink-0" />
          <span className="font-mono-d text-xs text-app-subtle">{selected.size} selected</span>
          <div className="hidden sm:block flex-1" />
          <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:ml-auto">
            <button onClick={() => runBulk("pause_ea")} data-testid="bulk-pause" className="btn-touch text-xs px-3 py-2 border border-app-border hover:bg-app-hover flex items-center gap-1.5 flex-1 sm:flex-none justify-center"><Pause size={12} weight="fill" /> Pause EA</button>
            <button onClick={() => runBulk("resume_ea")} data-testid="bulk-resume" className="btn-touch text-xs px-3 py-2 border border-app-border hover:bg-app-hover flex items-center gap-1.5 flex-1 sm:flex-none justify-center"><Play size={12} weight="fill" /> Resume</button>
            <button onClick={() => runBulk("close_all")} data-testid="bulk-close" className="btn-touch text-xs px-3 py-2 bg-[#FF3B30] hover:bg-[#FF5C53] flex items-center gap-1.5 flex-1 sm:flex-none justify-center"><X size={12} weight="bold" /> Close Trades</button>
            {isAdmin && (
              <button onClick={bulkRemove} data-testid="bulk-delete" className="btn-touch text-xs px-3 py-2 border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1.5 flex-1 sm:flex-none justify-center"><Trash size={12} /> Remove</button>
            )}
            <button onClick={() => setSelected(new Set())} className="btn-touch text-xs text-app-subtle hover:text-app-text px-3">Clear</button>
          </div>
        </div>
      )}

      {error && <div className="mb-4 px-3 py-2 border border-[#FF3B30]/30 text-[#FF6B61] text-xs font-mono-d">{error}</div>}

      {/* Mobile card list */}
      <div className="lg:hidden space-y-3 mb-4">
        {pageList.map((a) => (
          <AccountCard
            key={a.id}
            account={a}
            canControl={canControl}
            canRemove={isAdmin}
            selected={selected.has(a.id)}
            onToggle={() => toggle(a.id)}
            onOpen={() => navigate(`/accounts/${a.id}`)}
            onRemove={removeAccount}
            groups={groups}
            onGroupChange={assignGroup}
          />
        ))}
        {pageList.length === 0 && (
          <div className="border border-app-border bg-app-surface px-4 py-12 text-center text-app-muted font-mono-d text-xs">NO ACCOUNTS</div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block border border-app-border bg-app-surface table-scroll">
        <table className="w-full min-w-[1100px]" data-testid="accounts-table">
          <thead>
            <tr className="border-b border-app-border text-left">
              {canControl && <th className="w-10 px-4 py-3"></th>}
              {["Account", "Broker", "Start Bal", "Close Bal", "Max DD", "Profit (Today)", "Positions", "VPS", "EA", ...(canControl ? ["Group"] : []), ...(isAdmin ? ["Actions"] : [])].map((h) => (
                <th key={h} className="px-3 py-3 font-mono-d text-[10px] uppercase tracking-widest text-app-subtle">
                  <span className="inline-flex items-center gap-1 cursor-pointer hover:text-app-text"
                    onClick={() => { if (sortHeaders[h]) { setSortBy(sortHeaders[h]); setSortDir(sortDir === "asc" ? "desc" : "asc"); }}}>
                    {h} {Object.keys(sortHeaders).includes(h) && <CaretUpDown size={10} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageList.map((a) => {
              const m = a.metrics || {};
              return (
                <tr key={a.id} data-testid={`account-row-${a.account_number}`} className="border-b border-app-border/70 hover:bg-app-hover cursor-pointer transition-colors"
                  onClick={() => navigate(`/accounts/${a.id}`)}>
                  {canControl && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" data-testid={`row-check-${a.account_number}`} checked={selected.has(a.id)} onChange={() => toggle(a.id)} className="accent-[#007AFF]" />
                    </td>
                  )}
                  <td className="px-3 py-3">
                    <AccountIdentity
                      group={a.group}
                      groupColor={a.group_color}
                      accountNumber={a.account_number}
                      accountName={a.account_name}
                      bold
                    />
                  </td>
                  <td className="px-3 py-3 text-sm text-app-subtle">{a.broker}</td>
                  <SessionMetricCells m={m} />
                  <td className="px-3 py-3 font-mono-d text-sm">{m.open_positions ?? 0}</td>
                  <td className="px-3 py-3">
                    <span className="inline-flex items-center gap-1.5 font-mono-d text-[10px] uppercase">
                      <Circle size={8} weight="fill" className={m.vps_online ? "text-[#10B981]" : "text-[#FF3B30]"} />
                      {m.vps_online ? `${m.ping ?? 0}ms` : "Offline"}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-2 py-0.5 font-mono-d text-[10px] uppercase tracking-wider border ${
                      !m.ea_connected ? "border-[#666]/40 text-app-muted" :
                      m.ea_running ? "border-[#10B981]/40 text-[#10B981]" : "border-[#FF9500]/40 text-[#FF9500]"
                    }`}>
                      {!m.ea_connected ? "Offline" : m.ea_running ? "Running" : "Paused"}
                    </span>
                  </td>
                  {canControl && (
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <GroupSelect
                        compact
                        value={a.group}
                        groups={groups}
                        onChange={(g) => assignGroup(a.id, g)}
                      />
                    </td>
                  )}
                  {isAdmin && (
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={() => removeAccount(a.id, a.account_number)}
                        data-testid={`del-${a.account_number}`}
                        className="btn-touch px-2 py-1 text-xs border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 flex items-center gap-1"
                      >
                        <Trash size={12} />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {pageList.length === 0 && (
              <tr><td colSpan={canControl ? (isAdmin ? 12 : 11) : (isAdmin ? 11 : 10)} className="px-4 py-12 text-center text-app-muted font-mono-d text-xs">NO ACCOUNTS</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mt-4">
        <div className="font-mono-d text-[10px] text-app-muted uppercase tracking-widest">Page {page} / {totalPages} · {filtered.length} accounts</div>
        <div className="flex gap-2">
          <button data-testid="prev-page" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="btn-touch flex-1 sm:flex-none px-4 py-2 text-xs border border-app-border hover:bg-app-hover disabled:opacity-30">Prev</button>
          <button data-testid="next-page" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
            className="btn-touch flex-1 sm:flex-none px-4 py-2 text-xs border border-app-border hover:bg-app-hover disabled:opacity-30">Next</button>
        </div>
      </div>
    </PageShell>
  );
}
